import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  SnackbarCloseReason,
} from "@mui/material";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import {
  VForm,
  useVForm,
  IVFormErrors,
  VTextFieldData,
  VTextFieldNumber,
  AutoCompleteFuncionario,
  AutoCompleteConfProdutividade,
  VFieldMoney,
  AutoCompletePivotfazenda,
} from "../../../shared/forms";
import { dataextenso } from "../../../shared/services/util/dataextenso";
import { FerramentasDeDetalhe } from "../../../shared/components";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import {
  LanProducaoService,
  ILanProducao,
} from "../../../shared/services/api/lanProducao/lanProducao";
import { Environment } from "../../../shared/environment";

interface IFormData {
  datalan: Date;
  idconf: string;
  idfun: string;
  idpivot: string;
  qtd: number;
  meta: number;
  vlproducao: number;
  acmeta: number;
  vladicional: number;
  total: number;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  datalan: yup.date().required("Informe a data"),
  idconf: yup.string().required("Selecione uma produção"),
  idfun: yup.string().required("Selecione um funcionário"),
  idpivot: yup.string().required(),
  qtd: yup.number().required("Informe a quantidade"),
  meta: yup.number().required(),
  vlproducao: yup.number().required(),
  acmeta: yup.number().required(),
  vladicional: yup.number().required(),
  total: yup.number().required(),
});

export const Producao: React.FC = () => {
  const { id = "novo" } = useParams<"id">();
  const { formRef, save } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [urlimg, setUrlimg] = useState("");
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState("");
  const [tms, setTms] = useState<"warning" | "error" | "info" | "success">(
    "warning",
  );
  const [qtd, setQtd] = useState<number>(1);
  const [confDados, setConfDados] = useState({
    meta: 0,
    vladicional: 0,
    vlproducao: 0,
  });

  const idProducaoRef = useRef<string>("");

  const calcularTotal = (qtd: number, dados: typeof confDados) => {
    let acimaDaMeta = 0;
    let total = 0;
    if (qtd > dados.meta) {
      acimaDaMeta = qtd - dados.meta;
      total = dados.vlproducao + acimaDaMeta * dados.vladicional;
    } else {
      total = dados.vlproducao;
    }
    formRef.current?.setFieldValue("acmeta", acimaDaMeta);
    formRef.current?.setFieldValue("vladicional", String(dados.vladicional));
    formRef.current?.setFieldValue("total", String(total));
  };

  const handleConfSelecionado = (
    dados:
      | {
          idproducao: string;
          meta: number;
          vladicional: number;
          vlproducao: number;
        }
      | undefined,
  ) => {
    if (dados) {
      idProducaoRef.current = dados.idproducao;
      setConfDados(dados);
      formRef.current?.setFieldValue("meta", String(dados.meta));
      formRef.current?.setFieldValue("vlproducao", String(dados.vlproducao));
      formRef.current?.setFieldValue("vladicional", String(dados.vladicional));
      calcularTotal(qtd, dados);
    } else {
      idProducaoRef.current = "";
      const zerado = { meta: 0, vladicional: 0, vlproducao: 0 };
      setConfDados(zerado);
      formRef.current?.setFieldValue("meta", "0");
      formRef.current?.setFieldValue("vladicional", "0");
      formRef.current?.setFieldValue("vlproducao", "0");
      formRef.current?.setFieldValue("total", "0");
    }
  };

  const handleQuantidadeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const qtd = Number(e.target.value) || 0;
    setQtd(qtd);
    calcularTotal(qtd, confDados);
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") return;
    setOpenmsg(false);
    if (tms === "success") novoLancamento();
  };

  const dataFormatada = (): Date => {
    const dataAtual = new Date();
    dataAtual.setDate(dataAtual.getDate() - 1);
    if (dataAtual.getDay() === 0) dataAtual.setDate(dataAtual.getDate() + 1);
    return dataAtual;
  };

  const resetForm = () => {
    formRef.current?.setData({
      idfun: "",
      historico: "",
      datalan: dataFormatada(),
      horaextra: 1,
      tipo: "1",
      meta: 0,
      vladicional: 0,
      vlproducao: 0,
      total: 0,
    });
    setUrlimg("");
    setQtd(1);
    idProducaoRef.current = "";
    setConfDados({ meta: 0, vladicional: 0, vlproducao: 0 });
  };

  const novoLancamento = () => resetForm();

  useEffect(() => {
    if (id === "novo") resetForm();
  }, [id]);

  const handlefoto = (urlimg: string | undefined) => {
    setUrlimg(urlimg ? Environment.URL_IMGFUNCIONARIO + urlimg : "/logo.png");
  };

  const handleSave = (dados: IFormData) => {
    formValidationSchema
      .validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        const idproducao = idProducaoRef.current;
        const data = dadosValidados.datalan;

        const body: ILanProducao = {
          idlanpro: "",
          idproducao,
          idfun: dadosValidados.idfun,
          idpivot: dadosValidados.idpivot,
          qtd: dadosValidados.qtd,
          datalan: data,
          periodo: dataextenso(data),
          vlatividade: Number(dadosValidados.vlproducao ?? 0),
          vladicional: Number(dadosValidados.vladicional ?? 0),
          meta: Number(dadosValidados.meta ?? 0),
          acmeta: Number(dadosValidados.acmeta ?? 0),
          total: Number(dadosValidados.total ?? 0),
        };
        setIsLoading(true);

        LanProducaoService.create(body).then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms("error");
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTms("success");
            setMsg("Lançamento Realizado com sucesso!");
            setOpenmsg(true);
          }
        });
      })
      .catch((errors: yup.ValidationError) => {
        console.log(errors);
        const validationErrors: IVFormErrors = {};
        errors.inner?.forEach((error) => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  return (
    <LayoutBaseDePagina
      titulo={id === "novo" ? "Produção" : ""}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          mostrarBotaoSalvar
          mostrarBotaoVoltar={false}
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={isLoading}
          mostrarBotaoNovo={false}
          mostrarBotaoApagar={false}
          mostrarBotaoImpressao={false}
          aoClicarEmSalvar={save}
        />
      }
    >
      <VForm
        ref={formRef}
        onSubmit={handleSave}
        placeholder=""
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={openmsg}
          autoHideDuration={900}
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {msg}
          </Alert>
        </Snackbar>

        <Box
          margin={1}
          display="flex"
          flexDirection="column"
          component={Paper}
          variant="outlined"
        >
          <Grid container direction="column" padding={2} spacing={2}>
            {isLoading && (
              <Grid item>
                <LinearProgress variant="indeterminate" />
              </Grid>
            )}
            <Grid container item direction="row" spacing={2}>
              <Grid item xs={6} sm={6} md={3} lg={2} xl={1.5}>
                <Box width="100%" marginRight={5}>
                  <Avatar
                    variant="rounded"
                    alt="F"
                    src={urlimg}
                    sx={{ width: "100%", height: 200 }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={12} md={9} lg={6} xl={4}>
                <Grid container item direction="row" spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <AutoCompleteFuncionario
                      isExternalLoading={id === "novo" ? isLoading : true}
                      urlfoto={handlefoto}
                    />
                  </Grid>

                  <Grid item xs={6} sm={6} md={6.7} lg={6.7} xl={6.7}>
                    <AutoCompleteConfProdutividade
                      isExternalLoading={id === "novo" ? isLoading : true}
                      onConfSelecionado={handleConfSelecionado}
                    />
                  </Grid>

                  <Grid item xs={6} sm={6} md={5.3} lg={5.3} xl={5.3}>
                    <AutoCompletePivotfazenda />
                  </Grid>

                  <Grid item xs={6} sm={6} md={4} lg={4} xl={4}>
                    <VTextFieldData
                      fullWidth
                      name="datalan"
                      disabled={isLoading}
                      label="Data"
                    />
                  </Grid>

                  <Grid item xs={3} sm={3} md={2.7} lg={2.7} xl={2.7}>
                    <VTextFieldNumber
                      fullWidth
                      size="small"
                      name="qtd"
                      disabled={isLoading}
                      label="Quantidade"
                      onChange={handleQuantidadeChange}
                    />
                  </Grid>

                  <Grid item xs={3} sm={3} md={2.7} lg={2.7} xl={2.7}>
                    <VFieldMoney
                      fullWidth
                      size="small"
                      name="meta"
                      disabled
                      label="Meta"
                    />
                  </Grid>

                  <Grid item xs={3} sm={3} md={2.6} lg={2.6} xl={2.6}>
                    <VTextFieldNumber
                      fullWidth
                      size="small"
                      name="acmeta"
                      disabled
                      label="A.C Meta"
                    />
                  </Grid>

                  <Grid item xs={3} sm={3} md={4} lg={4} xl={4}>
                    <VFieldMoney
                      fullWidth
                      size="small"
                      name="vlproducao"
                      disabled
                      label="Valor Atividade"
                    />
                  </Grid>

                  <Grid item xs={3} sm={3} md={2.7} lg={2.7} xl={2.7}>
                    <VFieldMoney
                      fullWidth
                      size="small"
                      name="vladicional"
                      disabled
                      label="Adicional"
                    />
                  </Grid>

                  <Grid item xs={3} sm={3} md={5.3} lg={5.3} xl={5.3}>
                    <VFieldMoney
                      fullWidth
                      size="small"
                      name="total"
                      disabled
                      label="Total"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </VForm>
    </LayoutBaseDePagina>
  );
};
